import React from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useContent } from '../content/ContentContext';
import { Reveal, Kicker } from '../components/Reveal';
import { SEO } from '../components/SEO';
import { Banner } from './Home';

const GOOGLE_URL = 'https://share.google/1PIQUtXzrh7tltcQY';

const Stars = () => (
  <div className="flex gap-1" aria-label="5 su 5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={16} className="fill-terracotta text-terracotta" />
    ))}
  </div>
);

export default function ChiSiamo() {
  const { t } = useLanguage();
  const { img, ct } = useContent();
  const a = t.about;

  return (
    <div data-testid="chi-siamo-page">
      <SEO title={t.meta.aboutTitle} description={t.meta.aboutDesc} />

      <section className="bg-emerald-deep grain relative pt-40 pb-24 md:pb-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <Kicker light>{a.kicker}</Kicker>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">{a.title}</h1>
            <p className="mt-6 max-w-2xl text-ivory/70 text-base md:text-lg leading-relaxed">{a.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="about-bio">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="img-frame rounded-full aspect-square overflow-hidden ring-1 ring-terracotta/40 shadow-[0_8px_40px_rgba(37,79,87,0.20)] max-w-md mx-auto">
                <img src={img('about-photo', '/images/emma.jpg')} alt={a.photoAlt} className="w-full h-full object-cover" data-testid="about-photo" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.15}>
              <h2 className="font-serif italic text-5xl md:text-6xl text-terracotta-dark">{a.bioTitle}</h2>
              <p className="mt-8 text-emerald-ink/75 text-base md:text-lg leading-relaxed">{ct('about_bio', a.bio)}</p>
            </Reveal>
            <div className="mt-12 grid grid-cols-2 gap-4">
              {a.stats.map((s, i) => (
                <Reveal key={s.value + i} delay={0.2 + i * 0.08}>
                  <div className="border border-emerald/10 bg-white rounded-sm px-6 py-5" data-testid={`about-stat-${i}`}>
                    <p className="font-serif text-4xl text-emerald">{s.value}</p>
                    <p className="mt-2 text-xs text-emerald-ink/60 leading-relaxed">{s.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ivory-soft border-y border-emerald/10" data-testid="about-reviews">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Kicker>{a.reviewsKicker}</Kicker>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-emerald tracking-tight max-w-2xl">{a.reviewsTitle}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {a.reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure className="h-full bg-white border border-emerald/10 rounded-sm p-8 flex flex-col transition-all duration-500 hover:shadow-[0_12px_50px_rgba(37,79,87,0.14)] hover:-translate-y-1" data-testid={`review-card-${i}`}>
                  <Quote size={26} className="text-terracotta/50" strokeWidth={1.5} />
                  <blockquote className="mt-5 text-sm md:text-base text-emerald-ink/75 leading-relaxed flex-1 font-serif italic">
                    “{r}”
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-emerald/10 flex items-center justify-between">
                    <Stars />
                    <span className="text-[11px] uppercase tracking-[0.2em] text-emerald/50">{a.reviewsSource}</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <a
              href={GOOGLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="inline-flex items-center gap-3 rounded-full border border-emerald/25 text-emerald px-8 py-4 text-sm font-medium tracking-wide transition-all duration-300 hover:border-terracotta hover:text-terracotta-dark hover:-translate-y-0.5"
            >
              {a.googleCta} <ExternalLink size={15} />
            </a>
          </Reveal>
        </div>
      </section>

      <Banner title={t.home.bannerTitle} text={t.home.bannerText} cta={t.home.bannerCta} />
    </div>
  );
}
